import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { GameState } from "../core/game-state"
import { AUDIO, EVENT, SCENE, TEX } from "../core/keys"
import { setMusicMenuVolume } from "../core/music-volume"
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  MAX_WAVES,
  SCENE_TUNING,
} from "../data/constants"
import type { SectorDefinition } from "../data/sectors"
import type { GameData, GameSprite, RunStage, Upgrade } from "../types"
import { applyUpgrade, getRandomUpgrades } from "../utils"
import type { EconomySystem } from "./economy-system"
import { getSfxPlayer } from "./sfx-generator"
import type { VfxManager } from "./vfx-manager"

interface RunTransitionsOptions {
  scene: Phaser.Scene
  economy: EconomySystem
  vfx: VfxManager
  enemyGroup: Phaser.Physics.Arcade.Group
  projectileGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  getDogSprite: () => GameSprite
  getGameData: () => GameData
  getWaveNumber: () => number
  isObserverMode: () => boolean
  restartObserverWave: (reason: "wave-complete" | "victory" | "defeat") => void
  handleVictory: () => void
  collectCoin: (value: number, x: number, y: number) => void
  clearPowerMode: () => void
  setMovementStateOverride: (state: "launching" | null) => void
  advanceWave: () => void
  syncWaveMetadata: () => void
  getCurrentSector: () => SectorDefinition
  spawnPortal?: (x: number, y: number, type: string) => void
}

/**
 * Owns run-stage transitions and between-wave scene flow.
 * It does not own wave timing or spawn pacing.
 */
export class RunTransitions {
  private preShopUpgradeChoices = false
  private summaryLaunchTween?: Phaser.Tweens.Tween
  private readonly options: RunTransitionsOptions

  constructor(options: RunTransitionsOptions) {
    this.options = options
  }

  private canTransition(from: RunStage, to: RunStage): boolean {
    switch (to) {
      case "waveSummary":
      case "paused":
        return from === "playing"
      case "playing":
        return from === "paused" || from === "upgradeChoice"
      case "upgradeChoice":
        return (
          from === "playing" ||
          from === "waveSummary" ||
          from === "upgradeChoice"
        )
      case "shop":
        return from === "waveSummary" || from === "upgradeChoice"
      default:
        return false
    }
  }

  private setRunStage(next: RunStage): void {
    const gameData = this.options.getGameData()
    gameData.state = next
    GameState.instance.runState = next
  }

  private transition(to: RunStage, onTransition: () => void): boolean {
    const from = this.options.getGameData().state
    if (!this.canTransition(from, to)) return false
    onTransition()
    this.setRunStage(to)
    return true
  }

  public startWaveEndSummary(): void {
    const gameData = this.options.getGameData()
    if (
      !this.transition("waveSummary", () => {
        this.options.scene.physics.pause()
        this.options.vfx.flashScreen(0x000000, 0, 1)
      })
    )
      return

    const combo = gameData.player.combo
    combo.state = "normal"
    combo.hits = 0
    combo.targetHits = Math.max(1, combo.targetHits)
    combo.nextEffect = "charge"
    combo.timer = 0
    combo.hitColors = []
    this.options.clearPowerMode()
    this.options.setMovementStateOverride("launching")

    const activeEnemies = (
      this.options.enemyGroup.getChildren() as GameSprite[]
    ).filter((sprite) => sprite.active)
    const activeCoins = (
      this.options.expCoinGroup.getChildren() as GameSprite[]
    ).filter((sprite) => sprite.active)
    const hero = this.options.getDogSprite()

    for (const child of this.options.projectileGroup.children) {
      const sprite = child as GameSprite
      if (sprite.active) {
        this.options.vfx.sparkEmitter.explode(2, sprite.x, sprite.y)
        sprite.destroy()
      }
    }

    let lastCompletionDelay = 0

    activeEnemies.forEach((enemy, index) => {
      const delay = index * SCENE_TUNING.waveSummaryEnemyStaggerMs
      lastCompletionDelay = Math.max(
        lastCompletionDelay,
        delay +
          SCENE_TUNING.waveSummaryFlightDurationMs +
          SCENE_TUNING.waveSummaryOpenShopBufferMs,
      )

      this.options.scene.time.delayedCall(delay, () => {
        if (!enemy.active) return

        this.options.vfx.explosionEmitter.explode(12, enemy.x, enemy.y)
        this.options.vfx.bumperEmitter.explode(6, enemy.x, enemy.y)

        this.options.scene.tweens.add({
          targets: enemy,
          alpha: 0,
          scaleX: 0.2,
          scaleY: 0.2,
          duration: SCENE_TUNING.waveSummaryEnemyShrinkDurationMs,
          onComplete: () => enemy.destroy(),
        })
      })
    })

    activeCoins.forEach((coin, index) => {
      const delay =
        activeEnemies.length * SCENE_TUNING.waveSummaryEnemyStaggerMs +
        index * SCENE_TUNING.waveSummaryCoinStaggerMs
      const coinData = coin.entityData
      const value = coinData?.kind === "coin" ? coinData.value : 0

      lastCompletionDelay = Math.max(
        lastCompletionDelay,
        delay +
          SCENE_TUNING.waveSummaryFlightDurationMs +
          SCENE_TUNING.waveSummaryOpenShopBufferMs,
      )

      this.options.scene.time.delayedCall(delay, () => {
        if (!coin.active || value <= 0) {
          coin.destroy()
          return
        }

        const startX = coin.x
        const startY = coin.y
        coin.destroy()
        this.createWaveSummaryRewardFlight(startX, startY, value)
      })
    })

    const baseSummaryDelay = 600
    const launchDelay = Math.max(lastCompletionDelay, baseSummaryDelay)

    if (
      this.options.spawnPortal &&
      window.location.search.includes("portal=true")
    ) {
      this.options.scene.time.delayedCall(launchDelay, () => {
        this.options.spawnPortal?.(GAME_WIDTH - 100, GAME_HEIGHT * 0.3, "exit")
      })
    }

    this.options.scene.time.delayedCall(launchDelay, () =>
      this.launchHeroToNextWave(hero),
    )
  }

  public applyUpgradeChoice(upgrade: Upgrade): void {
    const gameData = this.options.getGameData()
    gameData.player = applyUpgrade(gameData.player, upgrade)
    gameData.lastUpgradeName = upgrade.name
    gameData.lastUpgradeSource = "wave"
    EventBus.emit(EVENT.UPGRADE_CHOSEN, upgrade)

    this.options.scene.scene.stop(SCENE.UPGRADE_CHOICE)

    // Handle instant level-up items (Tempo Boost +1, Momentum +2)
    if (upgrade.id === "item-tempo-boost") {
      this.options.economy.grantXp(this.options.getGameData().player.expToLevel)
    } else if (upgrade.id === "item-momentum") {
      // Grant enough XP for 2 levels
      const player = this.options.getGameData().player
      const level1Xp = player.expToLevel
      const level2Xp = player.expToLevel * 1.1 // Rough estimate for next level
      this.options.economy.grantXp(level1Xp + level2Xp)
    }

    if (this.options.economy.pendingLevelUpChoices.length > 0) {
      this.options.scene.time.delayedCall(300, () => this.launchLevelUpChoice())
      return
    }

    if (this.preShopUpgradeChoices) {
      this.preShopUpgradeChoices = false
      this.openShop()
      return
    }

    this.transition("playing", () => {
      this.options.scene.physics.resume()
    })
  }

  public getUpgradeChoices(): Upgrade[] {
    return getRandomUpgrades(3, this.options.getGameData().player, "level")
  }

  public resumeAfterShop(): void {
    const gameData = this.options.getGameData()
    const gameState = GameState.instance
    const runProgress = gameState.restoreRunProgress()

    if (runProgress) {
      gameData.player = runProgress.player
      gameData.coins = runProgress.coins
      gameData.runDistance = runProgress.runDistance
    }
    gameData.coinsThisWave = 0
    gameData.scoreThisWave = 0
    gameData.enemiesKilledThisWave = 0
    gameData.player.waveGuardCharges = gameData.player.passives.waveGuardPerWave

    // The shop is opened with the upcoming wave already selected, so resume
    // should continue directly into the persisted current wave.
    let nextWave = runProgress?.currentWave ?? gameState.currentWave

    // Apply wave skipping from Cursed Ring
    const wavesSkipped = gameData.player.passives.wavesSkipped ?? 0
    if (wavesSkipped > 0) {
      nextWave += wavesSkipped
    }

    if (nextWave > MAX_WAVES) {
      this.options.handleVictory()
      return
    }

    this.options.scene.scene.stop(SCENE.SHOP)
    this.options.scene.scene.stop(SCENE.HUD)
    gameState.runState = "waveIntro"
    this.options.scene.scene.restart({ wave: nextWave })
  }

  public requestPause(): void {
    if (
      this.transition("paused", () => {
        this.options.scene.physics.pause()
        this.options.scene.scene.launch(SCENE.PAUSE)
      })
    ) {
      EventBus.emit(EVENT.GAME_PAUSED)
    }
  }

  public resumeGame(): void {
    if (
      this.transition("playing", () => {
        this.options.scene.physics.resume()
      })
    ) {
      EventBus.emit(EVENT.GAME_RESUMED)
    }
  }

  private createWaveSummaryRewardFlight(
    startX: number,
    startY: number,
    value: number,
  ): void {
    const dogSprite = this.options.getDogSprite()
    const targetX = dogSprite.x + 8
    const targetY = dogSprite.y
    const rewardCoin = this.options.scene.add.sprite(startX, startY, TEX.COIN_M)
    rewardCoin.play("anim_coin_m")
    rewardCoin.setDepth(1205)
    rewardCoin.setDisplaySize(24, 24)

    this.options.scene.tweens.add({
      targets: rewardCoin,
      x: targetX,
      y: targetY,
      scaleX: 0.2,
      scaleY: 0.2,
      alpha: 0.08,
      duration: SCENE_TUNING.waveSummaryFlightDurationMs,
      ease: "Cubic.easeIn",
      onComplete: () => {
        rewardCoin.destroy()
        this.options.collectCoin(value, targetX, targetY)
      },
    })
  }

  private launchHeroToNextWave(hero: GameSprite): void {
    this.summaryLaunchTween?.stop()
    getSfxPlayer(this.options.scene)?.play(AUDIO.SFX_THRUSTER, 0.7)

    // Reset hero state to match intro exactly
    hero.setAngle(-12)
    const body = hero.body as Phaser.Physics.Arcade.Body
    if (body) body.setVelocity(0, 0)

    const launchStartX = hero.x
    const launchStartY = hero.y
    const launchEndX = GAME_WIDTH + 160
    const launchState = { progress: 0 }

    const tailX = hero.x - 38
    const tailY = hero.y + 6

    this.options.vfx.startHeroLaunchExhaust(hero)

    // Initial burst matching WaveScene.startWaveIntro
    this.options.vfx.sparkEmitter.explode(8, tailX, tailY)
    this.options.vfx.smokeEmitter.explode(6, tailX, tailY)
    this.summaryLaunchTween = this.options.scene.tweens.add({
      targets: launchState,
      progress: 1,
      duration: SCENE_TUNING.waveIntroDurationMs,
      ease: "Sine.easeInOut",
      onUpdate: () => {
        const t = launchState.progress
        const waveLift = Math.sin(t * Math.PI * 6) * (18 * (1 - t) + 6)
        const pitch =
          Phaser.Math.Linear(-12, 4, t) + Math.sin(t * Math.PI * 4) * 3
        hero.setPosition(
          Phaser.Math.Linear(launchStartX, launchEndX, t),
          launchStartY + waveLift,
        )
        hero.setAngle(pitch)
      },
      onComplete: () => {
        this.options.vfx.stopHeroLaunchExhaust()
        hero.setAngle(-12)
        this.openShop()
      },
    })
  }

  private launchLevelUpChoice(): void {
    const level = this.options.economy.pendingLevelUpChoices.shift()
    if (!level) return

    const gameData = this.options.getGameData()
    const choices = getRandomUpgrades(3, gameData.player, "level")

    this.transition("upgradeChoice", () => {
      this.options.scene.physics.pause()
      setMusicMenuVolume(this.options.scene)
      this.options.scene.scene.launch(SCENE.UPGRADE_CHOICE, {
        wave: this.options.getWaveNumber(),
        choices,
      })
    })
  }

  /** Called immediately when a level-up happens during active play. */
  public triggerMidWaveLevelUp(): void {
    if (this.options.economy.pendingLevelUpChoices.length === 0) return
    const currentState = this.options.getGameData().state
    if (currentState !== "playing") return
    this.launchLevelUpChoice()
  }

  private launchSectorClearScreen(waveNumber: number): void {
    const sector = this.options.getCurrentSector()
    const gameData = this.options.getGameData()

    this.options.scene.scene.launch(SCENE.SECTOR_CLEAR, {
      waveNumber,
      sectorName: sector.name,
      enemiesKilled: gameData.enemiesKilledThisWave,
      scoreEarned: gameData.scoreThisWave,
      coinsCollected: gameData.coinsThisWave,
    })
  }

  public openShop(): void {
    const gameData = this.options.getGameData()
    const currentWaveNum = this.options.getWaveNumber()

    // If we just finished the final wave (boss 12), go straight to victory
    if (currentWaveNum >= MAX_WAVES) {
      this.options.handleVictory()
      return
    }

    if (this.options.isObserverMode()) {
      this.options.restartObserverWave("wave-complete")
      return
    }

    if (this.options.economy.pendingLevelUpChoices.length > 0) {
      this.preShopUpgradeChoices = true
      this.launchLevelUpChoice()
      return
    }

    this.options.economy.applyBetweenWavePassives()

    // Check if we're finishing a sector (boss wave) BEFORE advancing
    const currentSector = this.options.getCurrentSector()
    const justFinishedSector = currentSector.bossWave === currentWaveNum

    // Advance to next wave now so shop shows upcoming wave context
    this.options.advanceWave()
    this.options.syncWaveMetadata()

    // If we just finished a sector, show the celebratory screen
    if (justFinishedSector) {
      this.launchSectorClearScreen(currentWaveNum)
    }

    const gameState = GameState.instance
    gameState.captureRunProgress({
      player: gameData.player,
      score: gameData.score,
      coins: gameData.coins,
      currentWave: gameData.wave,
      runDistance: gameData.runDistance,
    })

    if (
      this.transition("shop", () => {
        this.options.scene.physics.pause()
        setMusicMenuVolume(this.options.scene)
        this.options.scene.scene.launch(SCENE.SHOP, {
          wave: gameData.wave,
          coins: gameData.coins,
          coinsThisWave: gameData.coinsThisWave,
          player: gameData.player,
        })
      })
    ) {
      EventBus.emit(EVENT.SHOP_OPENED)
    }
  }
}
